import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
export function TrustMiniSection() {
  const {
    lang
  } = useLanguage();
  const iconProps = {
    width: 24,
    height: 24,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const
  };
  const differentiators = [{
    icon: <svg {...iconProps} data-id="element-439">
          <circle cx="12" cy="8" r="4" data-id="element-440" />
          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" data-id="element-441" />
        </svg>,
    title: 'Partimos de quién eres',
    titleEn: 'We start from who you are',
    text: 'No creamos una presencia genérica. Construimos desde tu identidad, tus habilidades y tu propósito — para que lo que proyectes sea auténtico y atraiga a los clientes correctos.',
    textEn: "We don't create a generic presence. We build from your identity, your skills, and your purpose — so that what you project is authentic and attracts the right clients."
  }, {
    icon: <svg {...iconProps} data-id="element-442">
          <path d="M12 2L3 7v5c0 5 3.8 9.7 9 11 5.2-1.3 9-6 9-11V7L12 2z" data-id="element-443" />
        </svg>,
    title: 'Legal y digital en un solo lugar',
    titleEn: 'Legal and digital in one place',
    text: 'Soy Notaria Certificada en Florida y experta en presencia digital. Puedes formalizar tu empresa y lanzar tu negocio sin buscar a nadie más.',
    textEn: 'I am a Certified Notary in Florida and an expert in digital presence. You can formalize your company and launch your business without looking for anyone else.'
  }, {
    icon: <svg {...iconProps} data-id="element-444">
          <path d="M9 11l3 3L22 4" data-id="element-445" />
          <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" data-id="element-446" />
        </svg>,
    title: 'Sabes exactamente qué sigue',
    titleEn: "You know exactly what's next",
    text: 'Sin improvisación. Cada paso tiene claridad — qué se entrega, cuánto tarda y qué necesitas de tu parte.',
    textEn: 'No improvisation. Every step has clarity — what is delivered, how long it takes, and what you need on your end.'
  }, {
    icon: <svg {...iconProps} data-id="element-447">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" data-id="element-448" />
          <circle cx="9" cy="7" r="4" data-id="element-449" />
          <path d="M23 21v-2a4 4 0 00-3-3.87" data-id="element-450" />
          <path d="M16 3.13a4 4 0 010 7.75" data-id="element-451" />
        </svg>,
    title: 'Acompañamiento real',
    titleEn: 'Real accompaniment',
    text: 'No recibes un template y un PDF. Hay una persona detrás de cada proyecto que entiende tu contexto, tu mercado y tu momento.',
    textEn: "You don't receive a template and a PDF. There is a person behind every project who understands your context, your market, and your moment."
  }, {
    icon: <svg {...iconProps} data-id="element-452">
          <circle cx="12" cy="12" r="10" data-id="element-453" />
          <line x1="2" y1="12" x2="22" y2="12" data-id="element-454" />
          <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" data-id="element-455" />
        </svg>,
    title: 'Construido para el emprendedor con propósito',
    titleEn: 'Built for the purpose-driven entrepreneur',
    text: 'Entiendo lo que significa construir algo desde cero — y acompaño ese proceso con claridad y propósito.',
    textEn: 'I understand what it means to build something from scratch — and I accompany that process with clarity and purpose.'
  }];
  return <section id="diferencia" className="py-16 lg:py-20 bg-white" data-id="element-456">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-457">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-12 lg:gap-24 items-center" data-id="element-458">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} className="order-2 lg:order-1" data-id="element-459">
            <div className="flex flex-col items-start gap-4 mb-8" data-id="element-460">
              <span className="block w-[40px] h-[1px] bg-inkova-primary/30" data-id="element-461" />
              <span className="inline-block font-sans text-[11px] font-semibold tracking-[3px] uppercase text-inkova-primary" data-id="element-462">
                {lang === 'es' ? 'NUESTRA DIFERENCIA' : 'OUR DIFFERENCE'}
              </span>
            </div>

            <h2 className="text-[32px] lg:text-[48px] font-serif text-inkova-dark leading-[1.15] tracking-[-0.5px] mb-8" data-id="element-463">
              {lang === 'es' ? <>
                  No es una agencia. Es un proceso construido desde tu{' '}
                  <em className="italic text-inkova-lavender" data-id="element-464">esencia.</em>
                </> : <>
                  It's not an agency. It's a process built from your{' '}
                  <em className="italic text-inkova-lavender" data-id="element-465">essence.</em>
                </>}
            </h2>
            <p className="text-[15px] lg:text-[18px] text-inkova-dark/70 font-light leading-[1.7] lg:leading-[1.8] mb-12 lg:mb-16 max-w-[500px]" data-id="element-466">
              {lang === 'es' ? 'Hay mil opciones en internet. Inkova existe para quienes quieren algo diferente.' : 'There are a thousand options on the internet. Inkova exists for those who want something different.'}
            </p>

            <div className="flex flex-col gap-8" data-id="element-467">
              {differentiators.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8,
              ease: 'easeOut',
              delay: index * 0.15
            }} className="group flex items-start gap-[14px] relative pl-4 border-l-[4px] border-[#5A4579] transition-transform duration-200 ease-in-out hover:translate-x-1" data-id="element-468">
                  <div className="shrink-0 w-[40px] h-[40px] rounded-[10px] bg-[#F0EDFB] text-[#5A4579] flex items-center justify-center transition-colors duration-200 ease-in-out group-hover:bg-[#5A4579] group-hover:text-white" data-id="element-469">
                    {item.icon}
                  </div>
                  <div className="pt-0 flex-1" data-id="element-470">
                    <h4 className="text-[16px] font-sans font-bold text-[#1A1A2E] mb-1 leading-snug" data-id="element-471">
                      {lang === 'es' ? item.title : item.titleEn}
                    </h4>
                    <p className="text-[14px] text-[#4B5563] font-normal leading-[1.7] max-w-[460px]" data-id="element-472">
                      {lang === 'es' ? item.text : item.textEn}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.2,
          ease: 'easeOut'
        }} className="order-1 lg:order-2 relative overflow-hidden rounded-[16px] shadow-[0_12px_40px_rgba(0,0,0,0.08)] w-full" data-id="element-473">
            <img src="https://cdn.magicpatterns.com/uploads/gabk1esxGn5VqcA6Svx4xP/ChatGPT_Image_Jun_22,_2026,_02_59_00_PM.png" alt="Annysol Navarro reflexionando con una idea — Por qué Inkova" loading="lazy" className="w-full h-[400px] lg:h-[700px] object-cover object-top" data-id="element-474" />
          </motion.div>
        </div>
      </div>
    </section>;
}